import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Loader2 } from 'lucide-react';
import JSZip from 'jszip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FileTypeIcon from './FileTypeIcon';
import * as XLSX from 'xlsx';

const EXT_TO_ICON = {
    md: 'md', pdf: 'pdf', docx: 'docx', doc: 'doc',
    xlsx: 'xlsx', xls: 'xls', csv: 'csv',
    pptx: 'pptx', ppt: 'ppt',
    txt: 'txt', json: 'json',
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', svg: 'image', webp: 'image', bmp: 'image',
    js: 'code', ts: 'code', tsx: 'code', jsx: 'code', py: 'code', java: 'code',
    go: 'code', rs: 'code', rb: 'code', php: 'code', c: 'code', cpp: 'code', h: 'code',
    html: 'code', css: 'code', scss: 'code', sql: 'code', sh: 'code', yaml: 'code', yml: 'code',
    xml: 'code', toml: 'code', ini: 'code', cfg: 'code', env: 'code',
    zip: 'file', rar: 'file', '7z': 'file', tar: 'file', gz: 'file',
};

const TEXT_TYPES = new Set(['md', 'txt', 'json', 'code', 'csv']);
const IMAGE_TYPES = new Set(['image']);
const SPREADSHEET_TYPES = new Set(['xlsx', 'xls']);

function getIconType(filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    return EXT_TO_ICON[ext] || 'file';
}

function buildTree(zip) {
    const root = { name: '', children: [], isFolder: true };
    const folders = { '': root };

    const getFolder = (path) => {
        if (folders[path]) return folders[path];
        const parts = path.split('/');
        const name = parts.pop();
        const parentPath = parts.join('/');
        const parent = getFolder(parentPath);
        const folder = { name, path, children: [], isFolder: true };
        folders[path] = folder;
        parent.children.push(folder);
        return folder;
    };

    zip.forEach((relativePath, entry) => {
        if (entry.dir) {
            getFolder(relativePath.replace(/\/$/, ''));
            return;
        }
        const parts = relativePath.split('/');
        const fileName = parts.pop();
        const folderPath = parts.join('/');
        const parent = folderPath ? getFolder(folderPath) : root;
        parent.children.push({
            name: fileName,
            path: relativePath,
            isFolder: false,
            iconType: getIconType(fileName),
        });
    });

    const sort = (node) => {
        if (!node.children) return;
        node.children.sort((a, b) => {
            if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        node.children.forEach(sort);
    };
    sort(root);

    if (root.children.length === 1 && root.children[0].isFolder) {
        return root.children[0];
    }
    return root;
}

function TreeNode({ node, depth = 0, selectedPath, onSelect, expanded, onToggle }) {
    const isExpanded = expanded.has(node.path || '');

    if (node.isFolder) {
        const hasChildren = node.children && node.children.length > 0;
        return (
            <div>
                <button
                    onClick={() => onToggle(node.path || '')}
                    className={`w-full flex items-center gap-1 py-1 px-2 text-left hover:bg-gray-100 transition-colors text-[13px]`}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                >
                    {hasChildren ? (
                        isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    ) : (
                        <span className="w-3.5 flex-shrink-0" />
                    )}
                    {isExpanded
                        ? <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        : <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    }
                    <span className="truncate text-gray-700">{node.name || 'root'}</span>
                </button>
                {isExpanded && node.children?.map((child) => (
                    <TreeNode
                        key={child.path || child.name}
                        node={child}
                        depth={depth + 1}
                        selectedPath={selectedPath}
                        onSelect={onSelect}
                        expanded={expanded}
                        onToggle={onToggle}
                    />
                ))}
            </div>
        );
    }

    const isSelected = node.path === selectedPath;
    return (
        <button
            onClick={() => onSelect(node.path)}
            className={`w-full flex items-center gap-1.5 py-1 px-2 text-left transition-colors text-[13px] ${
                isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'
            }`}
            style={{ paddingLeft: `${depth * 16 + 8 + 18}px` }}
        >
            <FileTypeIcon iconType={node.iconType} size={16} />
            <span className="truncate">{node.name}</span>
        </button>
    );
}

function collectAllFolders(node, set = new Set()) {
    if (node.isFolder) {
        set.add(node.path || '');
        node.children?.forEach(c => collectAllFolders(c, set));
    }
    return set;
}

function findFirstFile(node) {
    if (!node.isFolder) return node.path;
    for (const child of node.children || []) {
        const found = findFirstFile(child);
        if (found) return found;
    }
    return null;
}

function ZipFilePreview({ zip, filePath }) {
    const [content, setContent] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [workbook, setWorkbook] = useState(null);

    const iconType = useMemo(() => getIconType(filePath), [filePath]);
    const fileName = useMemo(() => filePath.split('/').pop(), [filePath]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setContent(null);
        setBlobUrl(null);
        setWorkbook(null);

        async function load() {
            const entry = zip.file(filePath);
            if (!entry) {
                if (!cancelled) setLoading(false);
                return;
            }

            try {
                if (TEXT_TYPES.has(iconType)) {
                    const text = await entry.async('string');
                    if (!cancelled) setContent(text);
                } else if (IMAGE_TYPES.has(iconType)) {
                    const blob = await entry.async('blob');
                    if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
                } else if (iconType === 'pdf') {
                    const blob = await entry.async('blob');
                    if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
                } else if (SPREADSHEET_TYPES.has(iconType)) {
                    const buf = await entry.async('arraybuffer');
                    const wb = XLSX.read(buf, { type: 'array' });
                    if (!cancelled) setWorkbook(wb);
                } else {
                    const text = await entry.async('string');
                    if (!cancelled) setContent(text);
                }
            } catch {
                if (!cancelled) setContent(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [zip, filePath, iconType]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
        );
    }

    if (iconType === 'md' && content != null) {
        return (
            <div className="prose prose-sm max-w-none px-6 py-4 overflow-y-auto h-full">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
        );
    }

    if (iconType === 'csv' && content != null) {
        return <ZipSpreadsheet csvContent={content} />;
    }

    if (SPREADSHEET_TYPES.has(iconType) && workbook) {
        return <ZipSpreadsheet workbook={workbook} />;
    }

    if (IMAGE_TYPES.has(iconType) && blobUrl) {
        return (
            <div className="flex items-center justify-center p-6 h-full">
                <img src={blobUrl} alt={fileName} className="max-w-full max-h-full object-contain rounded-lg" />
            </div>
        );
    }

    if (iconType === 'pdf' && blobUrl) {
        return <iframe src={blobUrl} className="w-full h-full border-0" title={fileName} />;
    }

    if (TEXT_TYPES.has(iconType) && content != null) {
        return (
            <pre className="px-6 py-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-auto h-full">
                {content}
            </pre>
        );
    }

    if (content != null && typeof content === 'string' && content.length < 500000) {
        return (
            <pre className="px-6 py-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-auto h-full">
                {content}
            </pre>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center gap-4 h-full">
            <FileTypeIcon iconType={iconType} size={48} />
            <p className="text-sm font-medium text-gray-700">{fileName}</p>
            <p className="text-xs text-gray-400">Không hỗ trợ xem trước định dạng này</p>
        </div>
    );
}

function colLabel(idx) {
    let label = '';
    let n = idx;
    while (n >= 0) {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    }
    return label;
}

function ZipSpreadsheet({ workbook: wbProp, csvContent }) {
    const [activeSheet, setActiveSheet] = useState(0);
    const [selectedCell, setSelectedCell] = useState({ r: 0, c: 0 });

    const workbook = useMemo(() => {
        if (wbProp) return wbProp;
        if (csvContent != null) {
            try { return XLSX.read(csvContent, { type: 'string' }); }
            catch { return null; }
        }
        return null;
    }, [wbProp, csvContent]);

    const sheetData = useMemo(() => {
        if (!workbook) return null;
        const name = workbook.SheetNames[activeSheet];
        const sheet = workbook.Sheets[name];
        if (!sheet) return null;
        const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
        const totalCols = Math.max(range.e.c + 1, 1);
        const totalRows = Math.max(range.e.r + 1, 1);
        const displayCols = Math.max(totalCols, 10);
        const displayRows = Math.max(totalRows, 20);
        const rows = [];
        for (let r = 0; r < displayRows; r++) {
            const row = [];
            for (let c = 0; c < displayCols; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[addr];
                row.push(cell ? XLSX.utils.format_cell(cell) : '');
            }
            rows.push(row);
        }
        return { rows, totalCols: displayCols };
    }, [workbook, activeSheet]);

    if (!sheetData) return <div className="flex items-center justify-center h-full text-sm text-gray-400">Không thể đọc file</div>;

    const selectedAddr = colLabel(selectedCell.c) + (selectedCell.r + 1);
    const selectedValue = sheetData.rows[selectedCell.r]?.[selectedCell.c] || '';

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="w-14 text-center text-xs font-medium text-gray-600 border border-gray-300 rounded px-1.5 py-1 bg-white">{selectedAddr}</div>
                <div className="flex-1 text-xs text-gray-700 border border-gray-300 rounded px-2 py-1 bg-white truncate">{selectedValue}</div>
            </div>
            <div className="flex-1 overflow-auto">
                <table className="border-collapse w-max min-w-full">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            <th className="w-12 min-w-[48px] h-7 bg-gray-100 border border-gray-300 text-[11px] text-gray-500 font-medium sticky left-0 z-20" />
                            {Array.from({ length: sheetData.totalCols }, (_, c) => (
                                <th key={c} className={`min-w-[80px] h-7 border border-gray-300 text-[11px] font-medium px-1 ${selectedCell.c === c ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {colLabel(c)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sheetData.rows.map((row, r) => (
                            <tr key={r}>
                                <td className={`w-12 min-w-[48px] h-7 border border-gray-300 text-[11px] font-medium text-center sticky left-0 z-10 ${selectedCell.r === r ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {r + 1}
                                </td>
                                {row.map((cell, c) => {
                                    const isSelected = selectedCell.r === r && selectedCell.c === c;
                                    return (
                                        <td key={c} onClick={() => setSelectedCell({ r, c })} className={`min-w-[80px] h-7 border text-[12px] text-gray-800 px-1.5 cursor-cell whitespace-nowrap ${isSelected ? 'border-2 border-blue-500 bg-white' : 'border-gray-200 bg-white hover:bg-blue-50/30'}`}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {workbook && workbook.SheetNames.length > 1 && (
                <div className="flex items-center border-t border-gray-300 bg-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-1 px-2 py-1">
                        {workbook.SheetNames.map((name, idx) => (
                            <button key={name} onClick={() => { setActiveSheet(idx); setSelectedCell({ r: 0, c: 0 }); }} className={`px-3 py-1 text-xs rounded-t border border-b-0 transition-colors ${idx === activeSheet ? 'bg-white text-gray-800 font-medium border-gray-300' : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-200'}`}>
                                {name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ZipViewer({ downloadUrl }) {
    const [zip, setZip] = useState(null);
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPath, setSelectedPath] = useState(null);
    const [expanded, setExpanded] = useState(new Set());

    useEffect(() => {
        if (!downloadUrl) return;
        let cancelled = false;

        async function load() {
            try {
                const res = await fetch(downloadUrl);
                const buf = await res.arrayBuffer();
                const z = await JSZip.loadAsync(buf);
                if (cancelled) return;
                setZip(z);
                const t = buildTree(z);
                setTree(t);
                const allFolders = collectAllFolders(t);
                setExpanded(allFolders);
                const firstFile = findFirstFile(t);
                if (firstFile) setSelectedPath(firstFile);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [downloadUrl]);

    const handleToggle = useCallback((path) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
        );
    }

    if (error || !zip || !tree) {
        return (
            <div className="flex items-center justify-center h-full text-sm text-red-500">
                {error || 'Không thể đọc file ZIP'}
            </div>
        );
    }

    return (
        <div className="flex h-full">
            <div className="w-60 border-r border-gray-200 overflow-y-auto flex-shrink-0 bg-gray-50/50 py-1">
                <TreeNode
                    node={tree}
                    selectedPath={selectedPath}
                    onSelect={setSelectedPath}
                    expanded={expanded}
                    onToggle={handleToggle}
                />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
                {selectedPath ? (
                    <ZipFilePreview zip={zip} filePath={selectedPath} />
                ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-400">
                        Chọn file để xem trước
                    </div>
                )}
            </div>
        </div>
    );
}
